import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /**
   * Scroll position captured before the drawer opens, so we can cancel the
   * browser's details scroll-into-view jump on mobile.
   * @type {number | null}
   */
  #savedScrollY = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupHeroLogoLink();
  }

  /**
   * Restore the pre-open scroll position without a smooth animation.
   */
  #restoreScrollPosition() {
    if (this.#savedScrollY == null) return;

    window.scrollTo(0, this.#savedScrollY);
  }

  #setupHeroLogoLink() {
    const link = this.closest('#header-component')?.querySelector('.header__hero-logo-link');
    if (!link || link.dataset.heroLogoBound === 'true') return;

    link.dataset.heroLogoBound = 'true';
    link.addEventListener('click', () => {
      if (!this.refs.details.classList.contains('menu-open')) return;
      this.close();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    document.documentElement.removeAttribute('scroll-lock');
    document.documentElement.style.scrollBehavior = '';
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    const { details } = this.refs;
    return details.hasAttribute('open') || details.classList.contains('menu-open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    if (event instanceof Event) {
      event.preventDefault();
    }

    return this.isOpen ? this.close() : this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    if (event instanceof Event) {
      event.preventDefault();
    }

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    this.#savedScrollY = window.scrollY;
    document.documentElement.style.scrollBehavior = 'auto';
    document.documentElement.setAttribute('scroll-lock', '');

    if (!details.hasAttribute('open')) {
      details.setAttribute('open', '');
    }

    this.#restoreScrollPosition();
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      this.#restoreScrollPosition();
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
        this.#restoreScrollPosition();
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    reset(details);

    document.documentElement.removeAttribute('scroll-lock');
    document.documentElement.style.scrollBehavior = '';

    if (details === this.refs.details) {
      this.#restoreScrollPosition();
      this.#savedScrollY = null;
      removeTrapFocus();
      const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
      openDetails.forEach(reset);
      return;
    }

    setTimeout(() => {
      trapFocus(this.refs.details);
    }, 0);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
